import { useMemo, useState } from 'react';
import axios from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import {
  AccessTimeRounded,
  AddRounded,
  CheckCircleRounded,
  EditRounded,
  PauseCircleRounded,
  PlayArrowRounded,
  RefreshRounded,
  WarningAmberRounded,
  WorkOutlineRounded,
} from '@mui/icons-material';

import ProaDatePicker from '../../../components/Common/ProaDatePicker';
import ProaTimePicker from '../../../components/Common/ProaTimePicker';
import { getPlatformAdminContext } from '../../platformAdmin/services/platformAdminService';
import {
  bootstrapStacContext,
  createStacQuickTask,
  finishStacCurrentActivity,
  getStacContext,
  getStacMyDay,
  listStacMyTasks,
  startStacTask,
  updateStacTimeEntry,
} from '../services/stacService';
import type {
  StacTimeEntry,
} from '../types/stac';
import {
  invalidateStacTimeQueries,
} from '../querySync';


function getHttpStatus(error: unknown): number | undefined {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  return error.response?.status;
}


function getApiErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  const detail = error.response?.data?.detail;

  return typeof detail === 'string' && detail.trim()
    ? detail
    : fallback;
}


function getLocalDayKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}


function formatTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}


type LocalDateTimeParts = {
  date: string;
  time: string;
};


function toLocalDateTimeParts(
  value: string,
): LocalDateTimeParts {
  const date = new Date(value);

  const year = date.getFullYear();
  const month = String(
    date.getMonth() + 1
  ).padStart(2, '0');
  const day = String(
    date.getDate()
  ).padStart(2, '0');
  const hour = String(
    date.getHours()
  ).padStart(2, '0');
  const minute = String(
    date.getMinutes()
  ).padStart(2, '0');

  return {
    date: `${year}-${month}-${day}`,
    time: `${hour}:${minute}`,
  };
}


function buildLocalDateTime(
  dateValue: string,
  timeValue: string,
): string | null {
  const normalizedDate = dateValue.trim();
  const normalizedTime = timeValue.trim();

  if (
    !normalizedDate
    || !normalizedTime
  ) {
    return null;
  }

  return `${normalizedDate}T${normalizedTime}`;
}


export default function StacMyWorkTodayView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [bootstrapContextName, setBootstrapContextName] = useState('STAC');
  const [quickTaskTitle, setQuickTaskTitle] = useState('');

  const [editingEntry, setEditingEntry] = useState<StacTimeEntry | null>(null);
  const [editTaskId, setEditTaskId] = useState('');

  const [editStartDate, setEditStartDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');

  const [editEndDate, setEditEndDate] = useState('');
  const [editEndTime, setEditEndTime] = useState('');

  const todayKey = getLocalDayKey();
  const browserTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  );

  const contextQuery = useQuery({
    queryKey: ['stac-context'],
    queryFn: getStacContext,
    retry: false,
  });

  const contextStatus = contextQuery.isError
    ? getHttpStatus(contextQuery.error)
    : undefined;

  const platformAdminQuery = useQuery({
    queryKey: ['platform-admin-context'],
    queryFn: getPlatformAdminContext,
    enabled: contextStatus === 403,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const context = contextQuery.data;

  const tasksQuery = useQuery({
    queryKey: ['stac-tasks', context?.worker_id],
    queryFn: listStacMyTasks,
    enabled: Boolean(context),
    retry: false,
  });

  const dayQuery = useQuery({
    queryKey: [
      'stac-day',
      context?.worker_id,
      todayKey,
      browserTimezone,
    ],
    queryFn: () => getStacMyDay({
      day: todayKey,
      timezone: browserTimezone,
    }),
    enabled: Boolean(context),
    retry: false,
    refetchInterval: 60_000,
  });

  const refreshWork = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['stac-tasks'],
      }),
      invalidateStacTimeQueries(
        queryClient
      ),
    ]);
  };

  const bootstrapMutation = useMutation({
    mutationFn: bootstrapStacContext,
    onSuccess: async (createdContext) => {
      queryClient.setQueryData(
        ['stac-context'],
        createdContext,
      );

      enqueueSnackbar(
        'STAC activado correctamente.',
        { variant: 'success' },
      );

      await refreshWork();
    },
    onError: (error) => {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          'No se pudo activar STAC.',
        ),
        { variant: 'error' },
      );
    },
  });

  const quickTaskMutation = useMutation({
    mutationFn: createStacQuickTask,
    onSuccess: async (result) => {
      setQuickTaskTitle('');

      enqueueSnackbar(
        result.status === 'created_and_started'
          ? 'Trabajo creado e iniciado.'
          : 'Trabajo creado.',
        { variant: 'success' },
      );

      await refreshWork();
    },
    onError: (error) => {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          'No se pudo crear el trabajo.',
        ),
        { variant: 'error' },
      );
    },
  });

  const startTaskMutation = useMutation({
    mutationFn: startStacTask,
    onSuccess: async (result) => {
      enqueueSnackbar(
        result.status === 'already_active'
          ? 'Esta tarea ya estaba activa.'
          : 'Actividad iniciada.',
        { variant: 'success' },
      );

      await refreshWork();
    },
    onError: (error) => {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          'No se pudo iniciar la tarea.',
        ),
        { variant: 'error' },
      );
    },
  });

  const finishMutation = useMutation({
    mutationFn: finishStacCurrentActivity,
    onSuccess: async (result) => {
      enqueueSnackbar(
        result.status === 'no_active_entry'
          ? 'No había ninguna actividad activa.'
          : 'Actividad finalizada.',
        { variant: 'success' },
      );

      await refreshWork();
    },
    onError: (error) => {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          'No se pudo finalizar la actividad.',
        ),
        { variant: 'error' },
      );
    },
  });

  const updateEntryMutation = useMutation({
    mutationFn: ({
      entryId,
      taskId,
      startedAt,
      endedAt,
    }: {
      entryId: string;
      taskId: string;
      startedAt: string;
      endedAt: string | null;
    }) => updateStacTimeEntry(
      entryId,
      {
        task_id: taskId,
        started_at: new Date(startedAt).toISOString(),
        ended_at: endedAt
          ? new Date(endedAt).toISOString()
          : null,
      },
    ),
    onSuccess: async () => {
      setEditingEntry(null);

      enqueueSnackbar(
        'Franja actualizada.',
        { variant: 'success' },
      );

      await refreshWork();
    },
    onError: (error) => {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          'No se pudo actualizar la franja.',
        ),
        { variant: 'error' },
      );
    },
  });

  const handleQuickCreate = (startNow: boolean) => {
    const title = quickTaskTitle.trim();

    if (!title) {
      return;
    }

    quickTaskMutation.mutate({
      title,
      start_now: startNow,
    });
  };

  const handleOpenEdit = (entry: StacTimeEntry) => {
    const startParts = toLocalDateTimeParts(
      entry.started_at,
    );

    const endParts = entry.ended_at
      ? toLocalDateTimeParts(entry.ended_at)
      : null;

    setEditingEntry(entry);
    setEditTaskId(entry.task_id);

    setEditStartDate(startParts.date);
    setEditStartTime(startParts.time);

    setEditEndDate(
      endParts?.date ?? ''
    );
    setEditEndTime(
      endParts?.time ?? ''
    );
  };

  const handleSaveEdit = () => {
    if (
      !editingEntry
      || !editTaskId
      || !editStartDate
      || !editStartTime
    ) {
      return;
    }

    const hasAnyEndValue = Boolean(
      editEndDate
      || editEndTime
    );

    if (
      hasAnyEndValue
      && (
        !editEndDate
        || !editEndTime
      )
    ) {
      enqueueSnackbar(
        'Para indicar el fin debes completar fecha y hora.',
        { variant: 'warning' },
      );
      return;
    }

    const startedAt = buildLocalDateTime(
      editStartDate,
      editStartTime,
    );

    const endedAt = hasAnyEndValue
      ? buildLocalDateTime(
          editEndDate,
          editEndTime,
        )
      : null;

    if (!startedAt) {
      return;
    }

    if (
      endedAt
      && new Date(endedAt) <= new Date(startedAt)
    ) {
      enqueueSnackbar(
        'El fin debe ser posterior al inicio.',
        { variant: 'warning' },
      );
      return;
    }

    updateEntryMutation.mutate({
      entryId: editingEntry.id,
      taskId: editTaskId,
      startedAt,
      endedAt,
    });
  };

  if (contextQuery.isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha('#94a3b8', 0.16)}`,
          bgcolor: alpha('#0f172a', 0.78),
          p: 3,
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
        >
          <CircularProgress size={20} />
          <Typography sx={{ color: '#cbd5e1', fontWeight: 700 }}>
            Comprobando tu acceso STAC...
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (contextQuery.isError) {
    if (contextStatus === 403) {
      const canBootstrap =
        platformAdminQuery.data?.effective_permissions?.includes(
          'platform:manage'
        ) === true;

      return (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha('#f59e0b', 0.35)}`,
            bgcolor: alpha('#0f172a', 0.78),
            p: { xs: 2, md: 3 },
          }}
        >
          <Stack spacing={2}>
            <Alert severity="warning" variant="outlined">
              <Typography sx={{ fontWeight: 850, mb: 0.4 }}>
                Tu usuario todavía no tiene acceso STAC activo.
              </Typography>
              <Typography variant="body2">
                La autenticación PROA funciona correctamente, pero aún no
                existe un trabajador STAC asociado a tu usuario.
              </Typography>
            </Alert>

            {platformAdminQuery.isLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                  Comprobando permisos de activación...
                </Typography>
              </Stack>
            ) : null}

            {canBootstrap ? (
              <Box
                component="form"
                onSubmit={(event) => {
                  event.preventDefault();

                  const contextName = bootstrapContextName.trim();

                  if (!contextName) {
                    return;
                  }

                  bootstrapMutation.mutate({
                    context_name: contextName,
                  });
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                >
                  <TextField
                    label="Contexto empresarial"
                    value={bootstrapContextName}
                    onChange={(event) =>
                      setBootstrapContextName(event.target.value)
                    }
                    fullWidth
                    inputProps={{ maxLength: 160 }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<CheckCircleRounded />}
                    disabled={
                      bootstrapMutation.isPending
                      || !bootstrapContextName.trim()
                    }
                    sx={{
                      minWidth: 170,
                      minHeight: 56,
                      textTransform: 'none',
                      fontWeight: 850,
                    }}
                  >
                    Activar STAC
                  </Button>
                </Stack>
              </Box>
            ) : !platformAdminQuery.isLoading ? (
              <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                Un administrador de plataforma debe habilitar inicialmente
                el contexto STAC.
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      );
    }

    if (contextStatus === 409) {
      return (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: 3 }}>
          Hay más de un contexto STAC disponible. La selección multiempresa
          todavía no forma parte de este primer corte.
        </Alert>
      );
    }

    return (
      <Alert
        severity="error"
        variant="outlined"
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<RefreshRounded />}
            onClick={() => void contextQuery.refetch()}
          >
            Reintentar
          </Button>
        }
        sx={{ borderRadius: 3 }}
      >
        No se pudo comprobar el contexto STAC.
      </Alert>
    );
  }

  if (!context) {
    return (
      <Alert severity="error" variant="outlined" sx={{ borderRadius: 3 }}>
        No se recibió un contexto STAC válido.
      </Alert>
    );
  }

  const tasks = tasksQuery.data ?? [];
  const day = dayQuery.data;
  const activeEntry = day?.active_entry ?? null;
  const activeTaskId = activeEntry?.task_id ?? null;

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#94a3b8', 0.18)}`,
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          px: {
            xs: 2.5,
            md: 3,
          },
          py: {
            xs: 2.5,
            md: 3,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            lg: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'flex-start',
            lg: 'center',
          }}
          spacing={2}
        >
          <Stack
            spacing={1.25}
            sx={{
              maxWidth: 920,
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <WorkOutlineRounded
                sx={{
                  color: '#38bdf8',
                  fontSize: 26,
                }}
              />

              <Typography
                variant="h5"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                Mi trabajo de hoy
              </Typography>
            </Stack>

            <Typography
              variant="body1"
              sx={{
                color: '#cbd5e1',
                lineHeight: 1.7,
              }}
            >
              Registra tu actividad, inicia trabajo
              disponible y consulta cómo se compone
              tu jornada.
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                lineHeight: 1.65,
              }}
            >
              {context.context_name}
              {' · '}
              {new Intl.DateTimeFormat(
                'es-ES',
                {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                },
              ).format(
                new Date(
                  `${todayKey}T12:00:00`
                ),
              )}
            </Typography>
          </Stack>

          <Chip
            icon={
              <CheckCircleRounded />
            }
            label="STAC conectado"
            variant="outlined"
            sx={{
              color: '#86efac',
              borderColor:
                'rgba(74,222,128,0.30)',
              bgcolor:
                'rgba(74,222,128,0.06)',
              fontWeight: 800,
            }}
          />
        </Stack>
      </Paper>

      {day?.has_overlaps ? (
        <Alert severity="warning" variant="outlined" icon={<WarningAmberRounded />}>
          Hay franjas solapadas en la cronología de hoy. Puedes corregirlas
          directamente desde cada registro.
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2.5,
        }}
      >
        {[
          {
            label: 'Actividad actual',
            value: dayQuery.isLoading
              ? '—'
              : activeEntry
                ? 'En curso'
                : 'Sin actividad',
            helper: activeEntry
              ? activeEntry.task_title
              : 'No hay trabajo iniciado ahora',
          },
          {
            label: 'Trabajos asignados',
            value: tasksQuery.isLoading
              ? '—'
              : tasks.length,
            helper:
              'Disponibles para iniciar o cambiar',
          },
          {
            label: 'Registros hoy',
            value: dayQuery.isLoading
              ? '—'
              : day?.entries.length ?? 0,
            helper:
              'Franjas de actividad registradas',
          },
          {
            label: 'Integridad temporal',
            value: dayQuery.isLoading
              ? '—'
              : day?.has_overlaps
                ? 'Revisar'
                : 'Correcta',
            helper: day?.has_overlaps
              ? 'Hay franjas solapadas'
              : 'Sin solapamientos detectados',
          },
        ].map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={{
              borderRadius: 3,
              border:
                '1px solid rgba(148,163,184,0.16)',
              bgcolor:
                'rgba(15,23,42,0.58)',
              px: 2,
              py: 1.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#94a3b8',
              }}
            >
              {item.label}
            </Typography>

            <Typography
              variant="h5"
              sx={{
                color: '#f8fafc',
                fontWeight: 900,
                mt: 0.4,
              }}
            >
              {item.value}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: '#64748b',
                mt: 0.5,
                lineHeight: 1.45,
              }}
            >
              {item.helper}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg:
              'minmax(360px, 0.9fr) '
              + 'minmax(0, 1.35fr)',
          },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              border: `1px solid ${alpha('#94a3b8', 0.18)}`,
              bgcolor: 'rgba(15,23,42,0.66)',
              backdropFilter: 'blur(10px)',
              p: 3,
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeRounded sx={{ color: '#38bdf8' }} />
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Actividad actual
                </Typography>
              </Stack>

              {dayQuery.isLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                    Cargando actividad...
                  </Typography>
                </Stack>
              ) : activeEntry ? (
                <Stack spacing={1.2}>
                  <Typography sx={{ color: '#f8fafc', fontWeight: 850, fontSize: '1.02rem' }}>
                    {activeEntry.task_title}
                  </Typography>

                  <Chip
                    icon={<PlayArrowRounded />}
                    label={`${formatTime(activeEntry.started_at)} → Ahora`}
                    sx={{ alignSelf: 'flex-start', fontWeight: 800 }}
                  />

                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<PauseCircleRounded />}
                    disabled={finishMutation.isPending}
                    onClick={() => finishMutation.mutate()}
                    sx={{ textTransform: 'none', fontWeight: 850 }}
                  >
                    Finalizar
                  </Button>
                </Stack>
              ) : (
                <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                  No hay ninguna actividad activa.
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${alpha('#94a3b8', 0.14)}`,
              bgcolor: alpha('#0f172a', 0.72),
              p: 2.5,
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AddRounded sx={{ color: '#38bdf8' }} />
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Crear trabajo rápido
                </Typography>
              </Stack>

              <Box component="form">
                <Stack spacing={1}>
                  <TextField
                    label="¿En qué vas a trabajar?"
                    value={quickTaskTitle}
                    onChange={(event) => setQuickTaskTitle(event.target.value)}
                    fullWidth
                    inputProps={{ maxLength: 240 }}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button
                      type="button"
                      variant="outlined"
                      disabled={quickTaskMutation.isPending || !quickTaskTitle.trim()}
                      onClick={() => handleQuickCreate(false)}
                      sx={{ textTransform: 'none', fontWeight: 800 }}
                    >
                      Crear
                    </Button>

                    <Button
                      type="button"
                      variant="contained"
                      startIcon={<PlayArrowRounded />}
                      disabled={quickTaskMutation.isPending || !quickTaskTitle.trim()}
                      onClick={() => handleQuickCreate(true)}
                      sx={{ textTransform: 'none', fontWeight: 850 }}
                    >
                      Crear e iniciar
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Stack>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${alpha('#94a3b8', 0.14)}`,
            bgcolor: alpha('#0f172a', 0.72),
            p: 2.5,
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <WorkOutlineRounded sx={{ color: '#38bdf8' }} />
                <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
                  Trabajo disponible
                </Typography>
              </Stack>

              <Button
                size="small"
                startIcon={<RefreshRounded />}
                onClick={() => void refreshWork()}
                disabled={tasksQuery.isFetching || dayQuery.isFetching}
                sx={{ textTransform: 'none' }}
              >
                Actualizar
              </Button>
            </Stack>

            {tasksQuery.isLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                  Cargando tareas...
                </Typography>
              </Stack>
            ) : tasksQuery.isError ? (
              <Alert severity="error" variant="outlined">
                No se pudieron cargar tus tareas STAC.
              </Alert>
            ) : tasks.length ? (
              <Stack divider={<Divider flexItem />}>
                {tasks.map((task) => {
                  const isActive = task.id === activeTaskId;

                  return (
                    <Stack
                      key={task.id}
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      spacing={1.25}
                      sx={{ py: 1.2 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                          {task.title}
                        </Typography>

                        {task.project_name ? (
                          <Typography
                            sx={{
                              color: '#38bdf8',
                              fontSize: '0.76rem',
                              fontWeight: 750,
                              mt: 0.15,
                            }}
                          >
                            {task.project_name}
                          </Typography>
                        ) : null}

                        <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
                          {task.assigned_at
                            ? `Asignada ${new Intl.DateTimeFormat(undefined, {
                                day: '2-digit',
                                month: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              }).format(new Date(task.assigned_at))}`
                            : 'Asignada a ti'}
                        </Typography>
                      </Box>

                      <Button
                        variant={isActive ? 'outlined' : 'contained'}
                        startIcon={<PlayArrowRounded />}
                        disabled={startTaskMutation.isPending || isActive}
                        onClick={() => startTaskMutation.mutate(task.id)}
                        sx={{ textTransform: 'none', fontWeight: 850 }}
                      >
                        {isActive ? 'En curso' : activeEntry ? 'Cambiar' : 'Iniciar'}
                      </Button>
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
                Todavía no tienes tareas asignadas. Crea una arriba para empezar.
              </Typography>
            )}
          </Stack>
        </Paper>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#94a3b8', 0.18)}`,
          bgcolor: 'rgba(15,23,42,0.66)',
          backdropFilter: 'blur(10px)',
          p: 3,
        }}
      >
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ color: '#f8fafc', fontWeight: 900 }}>
              Cronología de hoy
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.78rem', mt: 0.25 }}>
              {todayKey} · {browserTimezone}
            </Typography>
          </Box>

          {dayQuery.isLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography sx={{ color: '#94a3b8', fontSize: '0.88rem' }}>
                Cargando cronología...
              </Typography>
            </Stack>
          ) : dayQuery.isError ? (
            <Alert severity="error" variant="outlined">
              No se pudo cargar tu cronología de hoy.
            </Alert>
          ) : day?.entries.length ? (
            <Stack spacing={0.75}>
              {day.entries.map((entry) => {
                const hasOverlap = day.overlap_entry_ids.includes(entry.id);

                return (
                  <Paper
                    key={entry.id}
                    elevation={0}
                    sx={{
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(
                        hasOverlap ? '#f59e0b' : '#94a3b8',
                        hasOverlap ? 0.35 : 0.12,
                      )}`,
                      bgcolor: 'rgba(2,6,23,0.28)',
                      px: 1.5,
                      py: 1.2,
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      spacing={1}
                    >
                      <Box>
                        <Typography sx={{ color: '#f8fafc', fontWeight: 800 }}>
                          {entry.task_title}
                        </Typography>
                        <Typography sx={{ color: hasOverlap ? '#fbbf24' : '#94a3b8', fontSize: '0.84rem' }}>
                          {formatTime(entry.started_at)} → {entry.ended_at
                            ? formatTime(entry.ended_at)
                            : 'Ahora'}
                          {hasOverlap ? ' · Solapamiento detectado' : ''}
                        </Typography>
                      </Box>

                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditRounded />}
                        onClick={() => handleOpenEdit(entry)}
                        sx={{ textTransform: 'none' }}
                      >
                        Editar
                      </Button>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
              Todavía no has registrado actividad hoy.
            </Typography>
          )}
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(editingEntry)}
        onClose={() => {
          if (!updateEntryMutation.isPending) {
            setEditingEntry(null);
          }
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border:
              '1px solid rgba(148,163,184,0.18)',
            bgcolor: '#0f172a',
            backgroundImage: 'none',
            color: '#f8fafc',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            pt: 2.5,
            pb: 1.25,
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="flex-start"
          >
            <EditRounded
              sx={{
                mt: 0.15,
                color: '#38bdf8',
              }}
            />

            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: '1.05rem',
                  fontWeight: 900,
                }}
              >
                Corregir franja
              </Typography>

              <Typography
                sx={{
                  mt: 0.3,
                  color: '#94a3b8',
                  fontSize: '0.84rem',
                }}
              >
                Ajusta el trabajo y el intervalo registrado.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2.5,
            pt: '12px !important',
            pb: 2.25,
          }}
        >
          <Stack spacing={2.25}>
            <TextField
              select
              size="small"
              label="Trabajo"
              value={editTaskId}
              onChange={(event) =>
                setEditTaskId(
                  event.target.value
                )
              }
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  color: '#f8fafc',
                  bgcolor:
                    'rgba(15,23,42,0.44)',
                },
                '& .MuiInputLabel-root': {
                  color: '#94a3b8',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor:
                    'rgba(148,163,184,0.28)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor:
                    'rgba(56,189,248,0.46)',
                },
              }}
            >
              {tasks.map((task) => (
                <MenuItem
                  key={task.id}
                  value={task.id}
                >
                  {task.title}
                </MenuItem>
              ))}
            </TextField>

            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                  fontWeight: 850,
                }}
              >
                Inicio
              </Typography>

              <Box
                sx={{
                  mt: 0.75,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'minmax(0, 1fr) 145px',
                  },
                  gap: 1.25,
                }}
              >
                <ProaDatePicker
                  label="Fecha"
                  value={editStartDate}
                  onChange={setEditStartDate}
                  required
                />

                <ProaTimePicker
                  label="Hora"
                  value={editStartTime}
                  onChange={setEditStartTime}
                  required
                />
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                  fontWeight: 850,
                }}
              >
                Fin
              </Typography>

              <Box
                sx={{
                  mt: 0.75,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'minmax(0, 1fr) 145px',
                  },
                  gap: 1.25,
                }}
              >
                <ProaDatePicker
                  label="Fecha"
                  value={editEndDate}
                  onChange={setEditEndDate}
                />

                <ProaTimePicker
                  label="Hora"
                  value={editEndTime}
                  onChange={setEditEndTime}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.75,
                  color: '#64748b',
                }}
              >
                Fecha en formato dd/mm/aaaa y hora en formato 24 h.
                Deja ambos campos de fin vacíos únicamente si la
                actividad debe permanecer abierta.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.75,
            borderTop:
              '1px solid rgba(148,163,184,0.14)',
          }}
        >
          <Button
            onClick={() =>
              setEditingEntry(null)
            }
            disabled={
              updateEntryMutation.isPending
            }
            sx={{
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={
              updateEntryMutation.isPending
              || !editTaskId
              || !editStartDate
              || !editStartTime
              || (
                Boolean(
                  editEndDate
                  || editEndTime
                )
                && (
                  !editEndDate
                  || !editEndTime
                )
              )
            }
            sx={{
              textTransform: 'none',
              fontWeight: 900,
              px: 2.25,
            }}
          >
            {updateEntryMutation.isPending
              ? 'Guardando...'
              : 'Guardar cambios'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
